import React, { useState, useEffect } from 'react';
import { doc, deleteDoc } from 'firebase/firestore';
import { db } from '../../lib/firebase';
import { Cog, Plus, Search, Edit, Trash2, RulerDimensionLine, Hash, X } from 'lucide-react';
import TypeForm from './TypeForm';
import SearchCounter from '../SearchCounter';
import { useAuth } from '../../contexts/AuthContext';
import { getAllTypes, checkTypeInUse } from '../../lib/typeService';
import { getAllSpecs } from '../../lib/specService';
import { matchesSearchTerm } from '../../lib/searchUtils';

const EquipmentTypesTab = () => {
  const { userRole } = useAuth();
  const [types, setTypes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [showForm, setShowForm] = useState(false);
  const [editingType, setEditingType] = useState(null);
  const [success, setSuccess] = useState('');
  const [error, setError] = useState('');
  const [specs, setSpecs] = useState([]);
  const [expandedTypes, setExpandedTypes] = useState(new Set());

  useEffect(() => {
    fetchTypes();
    fetchSpecs();
  }, []);

  const fetchTypes = async () => {
    try {
      setLoading(true);
      
      // Use local-first type service
      const typesList = await getAllTypes();
      setTypes(typesList);
    } catch (error) {
      console.error('Error fetching equipment types:', error);
      console.log('Tipos_equipo collection may not exist yet, starting with empty list');
      setTypes([]);
    } finally {
      setLoading(false);
    }
  };


  const fetchSpecs = async () => {
    try {
      // Use local-first spec service
      const specsList = await getAllSpecs();
      setSpecs(specsList);
    } catch (error) {
      console.log('Specs collection may not exist yet, starting with empty list');
      setSpecs([]);
    }
  };

  const getSpecNameById = (specId) => {
    const spec = specs.find(s => s.id === specId);
    return spec ? spec.nombre : 'Especificación desconocida';
  };

  const filteredTypes = types.filter(type => {
    // If no search term, include all types
    if (!searchTerm || searchTerm.trim() === '') {
      return true;
    }
    
    // Search by name
    if (type.nombre && matchesSearchTerm(type.nombre, searchTerm)) {
      return true;
    }
    
    // Search by specifications (new format)
    if (type.specsIds && Array.isArray(type.specsIds)) {
      return type.specsIds.some(specId => {
        const specName = getSpecNameById(specId);
        return matchesSearchTerm(specName, searchTerm);
      });
    }
    
    // Search by specifications (old format for backward compatibility)
    if (type.especificaciones && Array.isArray(type.especificaciones)) {
      return type.especificaciones.some(spec => 
        matchesSearchTerm(spec, searchTerm)
      );
    }
    
    return false;
  });

  const canEdit = () => {
    return userRole && ['admin', 'shopkeeper'].includes(userRole);
  };

  const canDelete = () => {
    return userRole === 'admin';
  };

  const canPerformActions = () => {
    return userRole && ['admin', 'shopkeeper'].includes(userRole);
  };

  const handleFormSuccess = async () => {
    const message = editingType ? 'Tipo de equipo actualizado exitosamente' : 'Tipo de equipo creado exitosamente';
    setSuccess(message);
    setEditingType(null);
    
    // Force refresh from Firestore to get the latest data
    try {
      setLoading(true);
      const typesList = await getAllTypes(true); // Force refresh
      setTypes(typesList);
    } catch (error) {
      console.error('Error refreshing equipment types:', error);
      setTypes([]);
    } finally {
      setLoading(false);
    }
    
    setTimeout(() => setSuccess(''), 5000);
  };

  const handleEdit = (type) => {
    setEditingType(type);
    setShowForm(true);
  };

  const handleCloseForm = () => {
    setShowForm(false);
    setEditingType(null);
  };


  const handleDelete = async (type) => {
    if (!canDelete()) {
      setError('No tienes permisos para eliminar tipos de equipo');
      setTimeout(() => setError(''), 5000);
      return;
    }

    try {
      // Check if type is used in brands or equipment
      const isInUse = await checkTypeInUse(type.nombre);
      
      if (isInUse) {
        setError(`No se puede eliminar el tipo "${type.nombre}" porque está siendo usado en marcas o equipos del catálogo.`);
        setTimeout(() => setError(''), 8000);
        return;
      }

      // Confirm deletion
      if (!window.confirm(
        `¿Está seguro de eliminar permanentemente el tipo de equipo "${type.nombre}"?\n\n` +
        `Esta acción no se puede deshacer.`
      )) {
        return;
      }

      // Delete the type
      await deleteDoc(doc(db, 'tipos_equipo', type.id));
      setSuccess(`Tipo de equipo "${type.nombre}" eliminado exitosamente`);
      fetchTypes();
      setTimeout(() => setSuccess(''), 5000);
    } catch (error) {
      console.error('Error deleting equipment type:', error);
      setError('Error al eliminar el tipo de equipo');
      setTimeout(() => setError(''), 5000);
    }
  };

  const formatSpecifications = (type) => {
    // Handle new format (specsIds array)
    if (type.specsIds && Array.isArray(type.specsIds)) {
      if (type.specsIds.length === 0) return 'Sin especificaciones definidas';
      
      const specNames = type.specsIds.map(id => getSpecNameById(id));
      
      if (specNames.length <= 3) {
        return specNames.join(', ');
      }
      
      return `${specNames.slice(0, 3).join(', ')} y ${specNames.length - 3} más`;
    }
    
    // Handle old format (especificaciones array) for backward compatibility
    if (type.especificaciones && Array.isArray(type.especificaciones)) {
      if (type.especificaciones.length === 0) return 'Sin especificaciones definidas';
      
      if (type.especificaciones.length <= 3) {
        return type.especificaciones.join(', ');
      }
      
      return `${type.especificaciones.slice(0, 3).join(', ')} y ${type.especificaciones.length - 3} más`;
    }
    
    return 'Sin especificaciones definidas';
  };

  const getSpecificationTags = (type) => {
    // Handle new format (specsIds array)
    if (type.specsIds && Array.isArray(type.specsIds)) {
      return type.specsIds.slice(0, 3).map(id => ({
        id,
        nombre: getSpecNameById(id)
      }));
    }
    
    // Handle old format (especificaciones array) for backward compatibility
    if (type.especificaciones && Array.isArray(type.especificaciones)) {
      return type.especificaciones.slice(0, 3).map((spec, index) => ({
        id: `legacy-${index}`,
        nombre: spec
      }));
    }
    
    return [];
  };

  const getSpecificationCount = (type) => {
    if (type.specsIds && Array.isArray(type.specsIds)) {
      return type.specsIds.length;
    }
    if (type.especificaciones && Array.isArray(type.especificaciones)) {
      return type.especificaciones.length;
    }
    return 0;
  };

  const toggleExpanded = (typeId) => {
    const newExpanded = new Set(expandedTypes);
    if (newExpanded.has(typeId)) {
      newExpanded.delete(typeId);
    } else {
      newExpanded.add(typeId);
    }
    setExpandedTypes(newExpanded);
  };

  const getAllSpecificationTags = (type) => {
    // Handle new format (specsIds array)
    if (type.specsIds && Array.isArray(type.specsIds)) {
      return type.specsIds.map(id => ({
        id,
        nombre: getSpecNameById(id)
      }));
    }
    
    // Handle old format (especificaciones array) for backward compatibility
    if (type.especificaciones && Array.isArray(type.especificaciones)) {
      return type.especificaciones.map((spec, index) => ({
        id: `legacy-${index}`,
        nombre: spec
      }));
    }
    
    return [];
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center space-x-3">
          <Cog className="h-5 w-5 sm:h-6 sm:w-6 text-green-600 dark:text-green-400 flex-shrink-0" />
          <h2 className="text-lg sm:text-xl font-semibold text-gray-900 dark:text-white">Tipos de Equipo</h2>
        </div>
        {canEdit() && (
          <button 
            onClick={() => setShowForm(true)}
            className="flex items-center justify-center w-10 h-10 bg-green-600 dark:bg-green-700 text-white rounded-md hover:bg-green-700 dark:hover:bg-green-800 focus:outline-none focus:ring-2 focus:ring-green-500 dark:focus:ring-green-400"
            title="Nuevo Tipo"
          >
            <Plus className="h-4 w-4" />
          </button>
        )}
      </div>

      {/* Search Bar */}
      <div className="space-y-4">
        {error && (
          <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 text-red-600 dark:text-red-400 px-4 py-3 rounded-md">
            {error}
          </div>
        )}
        
        {success && (
          <div className="bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800 text-green-600 dark:text-green-400 px-4 py-3 rounded-md">
            {success}
          </div>
        )}
        
        <div>
          <div className="relative">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400 dark:text-gray-500" />
            <input
              type="text"
              placeholder="Buscar tipos de equipo por nombre o especificaciones..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full pl-10 pr-10 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:outline-none focus:ring-2 focus:ring-green-500 dark:focus:ring-green-400 bg-white dark:bg-gray-700 text-gray-900 dark:text-white placeholder-gray-500 dark:placeholder-gray-400"
            />
            {searchTerm && (
              <button
                onClick={() => setSearchTerm('')}
                className="absolute right-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400 dark:text-gray-500 hover:text-gray-600 dark:hover:text-gray-300"
                title="Limpiar búsqueda"
              >
                <X className="h-4 w-4" />
              </button>
            )}
          </div>
          <SearchCounter 
            count={filteredTypes.length} 
            label="tipos de equipo" 
            isSearching={!!searchTerm} 
          />
        </div>
      </div>

      {/* Types List */}
      <div className="bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg">
        {loading ? (
          <div className="text-center py-8 text-gray-500 dark:text-gray-400">
            Cargando tipos de equipo...
          </div>
        ) : filteredTypes.length === 0 ? (
          <div className="text-center py-8 text-gray-500 dark:text-gray-400">
            {searchTerm ? 'No se encontraron tipos de equipo que coincidan con la búsqueda' : 'No hay tipos de equipo registrados'}
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
              <thead className="bg-gray-50 dark:bg-gray-700">
                <tr>
                  <th className="px-3 sm:px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                    Tipo de Equipo
                  </th>
                  <th className="px-3 sm:px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider hidden lg:table-cell">
                    Especificaciones Asociadas
                  </th>
                  <th className="px-3 sm:px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider hidden xl:table-cell">
                    Uso en Marcas
                  </th>
                  <th className="px-3 sm:px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider hidden md:table-cell">
                    Equipos en Catálogo
                  </th>
                  {canPerformActions() && (
                    <th className="px-3 sm:px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                      Acciones
                    </th>
                  )}
                </tr>
              </thead>
              <tbody className="bg-white dark:bg-gray-800 divide-y divide-gray-200 dark:divide-gray-700">
                {filteredTypes.map((type) => (
                  <tr key={type.id} className="hover:bg-gray-50 dark:hover:bg-gray-700">
                    <td className="px-3 sm:px-6 py-4">
                      <div className="flex items-center">
                        <Cog className="h-5 w-5 text-green-500 dark:text-green-400 mr-3 flex-shrink-0" />
                        <div className="min-w-0">
                          <div className="text-sm font-medium text-gray-900 dark:text-white truncate">
                            {type.nombre}
                          </div>
                          <div className="text-sm text-gray-500 dark:text-gray-400 truncate">
                            Creado: {new Date(type.createdAt?.toDate ? type.createdAt.toDate() : type.createdAt).toLocaleDateString('es-ES')}
                          </div>
                          {/* Mobile: Show specifications below name when column is hidden */}
                          <div className="mt-2 lg:hidden">
                            {getSpecificationCount(type) > 0 ? (
                              <div className="flex flex-wrap gap-1">
                                {(expandedTypes.has(type.id) ? getAllSpecificationTags(type) : getSpecificationTags(type)).map((spec) => (
                                  <span
                                    key={spec.id}
                                    className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-green-100 dark:bg-green-900/20 text-green-800 dark:text-green-400"
                                  >
                                    <RulerDimensionLine className="h-3 w-3 mr-1" />
                                    {spec.nombre}
                                  </span>
                                ))}
                                {getSpecificationCount(type) > 3 && !expandedTypes.has(type.id) && (
                                  <button
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      toggleExpanded(type.id);
                                    }}
                                    className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-green-100 dark:bg-green-900/20 text-green-800 dark:text-green-400 hover:bg-green-200 dark:hover:bg-green-900/30 cursor-pointer transition-colors"
                                    title="Ver todas las especificaciones"
                                  >
                                    +{getSpecificationCount(type) - 3}
                                  </button>
                                )}
                                {expandedTypes.has(type.id) && getSpecificationCount(type) > 3 && (
                                  <button
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      toggleExpanded(type.id);
                                    }}
                                    className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-green-100 dark:bg-green-900/20 text-green-800 dark:text-green-400 hover:bg-green-200 dark:hover:bg-green-900/30 cursor-pointer transition-colors"
                                    title="Mostrar menos"
                                  >
                                    −
                                  </button>
                                )}
                              </div>
                            ) : (
                              <span className="text-xs text-gray-500 dark:text-gray-400">Sin especificaciones definidas</span>
                            )}
                          </div>
                          {/* Mobile: Show usage counts below when columns are hidden */}
                          <div className="text-xs text-gray-500 dark:text-gray-400 mt-1 xl:hidden">
                            {type.brandUsageCount || 0} marcas
                          </div>
                          <div className="text-xs text-gray-500 dark:text-gray-400 mt-1 md:hidden">
                            {type.equipmentCount || 0} equipos en catálogo
                          </div>
                        </div>
                      </div>
                    </td>
                    <td className="px-3 sm:px-6 py-4 hidden lg:table-cell">
                      {getSpecificationCount(type) > 0 ? (
                        <div className="flex flex-wrap gap-1">
                          {(expandedTypes.has(type.id) ? getAllSpecificationTags(type) : getSpecificationTags(type)).map((spec) => (
                            <span
                              key={spec.id}
                              className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-green-100 dark:bg-green-900/20 text-green-800 dark:text-green-400"
                            >
                              <RulerDimensionLine className="h-3 w-3 mr-1" />
                              {spec.nombre}
                            </span>
                          ))}
                          {getSpecificationCount(type) > 3 && !expandedTypes.has(type.id) && (
                            <button
                              onClick={(e) => {
                                e.stopPropagation();
                                toggleExpanded(type.id);
                              }}
                              className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-green-100 dark:bg-green-900/20 text-green-800 dark:text-green-400 hover:bg-green-200 dark:hover:bg-green-900/30 cursor-pointer transition-colors"
                              title="Ver todas las especificaciones"
                            >
                              +{getSpecificationCount(type) - 3}
                            </button>
                          )}
                          {expandedTypes.has(type.id) && getSpecificationCount(type) > 3 && (
                            <button
                              onClick={(e) => {
                                e.stopPropagation();
                                toggleExpanded(type.id);
                              }}
                              className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-green-100 dark:bg-green-900/20 text-green-800 dark:text-green-400 hover:bg-green-200 dark:hover:bg-green-900/30 cursor-pointer transition-colors"
                              title="Mostrar menos"
                            >
                              −
                            </button>
                          )}
                        </div>
                      ) : (
                        <span className="text-sm text-gray-500 dark:text-gray-400">Sin especificaciones definidas</span>
                      )}
                    </td>
                    <td className="px-3 sm:px-6 py-4 whitespace-nowrap hidden xl:table-cell">
                      <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 dark:bg-blue-900/20 text-blue-800 dark:text-blue-400">
                        <Hash className="h-3 w-3 mr-1" />
                        {type.brandUsageCount || 0} marcas
                      </span>
                    </td>
                    <td className="px-3 sm:px-6 py-4 whitespace-nowrap hidden md:table-cell">
                      <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-purple-100 dark:bg-purple-900/20 text-purple-800 dark:text-purple-400">
                        {type.equipmentCount || 0} equipos
                      </span>
                    </td>
                    {canPerformActions() && (
                      <td className="px-3 sm:px-6 py-4 whitespace-nowrap text-sm font-medium">
                        <div className="flex items-center space-x-2">
                          {canEdit() && (
                            <button
                              onClick={() => handleEdit(type)}
                              className="p-2 text-blue-600 dark:text-blue-400 hover:text-blue-900 dark:hover:text-blue-300 hover:bg-blue-50 dark:hover:bg-blue-900/20 rounded"
                              title="Editar tipo"
                            >
                              <Edit className="h-4 w-4" />
                            </button>
                          )}
                          {canDelete() && (
                            <button
                              onClick={() => handleDelete(type)}
                              className={`p-2 rounded ${
                                (type.brandUsageCount > 0 || type.equipmentCount > 0)
                                  ? 'text-gray-400 dark:text-gray-600 cursor-not-allowed'
                                  : 'text-red-600 dark:text-red-400 hover:text-red-900 dark:hover:text-red-300 hover:bg-red-50 dark:hover:bg-red-900/20'
                              }`}
                              title={
                                (type.brandUsageCount > 0 || type.equipmentCount > 0)
                                  ? `No se puede eliminar - está siendo usado en ${type.brandUsageCount} marca(s) y ${type.equipmentCount} equipo(s)`
                                  : 'Eliminar tipo'
                              }
                              disabled={type.brandUsageCount > 0 || type.equipmentCount > 0}
                            >
                              <Trash2 className="h-4 w-4" />
                            </button>
                          )}
                        </div>
                      </td>
                    )}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Type Form Modal */}
      <TypeForm
        isOpen={showForm}
        onClose={handleCloseForm}
        onSuccess={handleFormSuccess}
        editingType={editingType}
      />
    </div>
  );
};

export default EquipmentTypesTab;